import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const { user }  = useAuth();
  const [today, setToday]       = useState(null);
  const [summary, setSummary]   = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [clock, setClock]       = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    fetchData();
    return () => clearInterval(t);
  }, []);

  const fetchData = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get('/attendance/today'),
        api.get('/attendance/summary?period=month'),
      ]);
      setToday(t.data);
      setSummary(s.data);
      if (user?.role !== 'employee') {
        const d = await api.get('/attendance/dashboard');
        setDashboard(d.data);
      }
    } catch (err) { console.error(err); }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const res = await api.post('/attendance/checkin');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Check-in failed';
      if (err.response?.data?.action === 'apply_wfh') {
        toast.error('Not on office network. Apply for WFH first.');
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const res = await api.post('/attendance/checkout');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Check-out failed');
    } finally { setLoading(false); }
  };

  const timeStr = clock.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  const dateStr = clock.toLocaleDateString('en-IN', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
  const checkedIn  = today?.check_in && !today?.check_out;
  const checkedOut = today?.check_in && today?.check_out;

  return (
    <div>
      <h1 className="page-title">Good {clock.getHours() < 12 ? 'Morning' : clock.getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.full_name?.split(' ')[0]} 👋</h1>

      {/* Check-in widget */}
      <div className="checkin-card">
        <div className="checkin-time">{timeStr}</div>
        <div className="checkin-date">{dateStr}</div>

        {checkedOut ? (
          <div>
            <div style={{fontSize:15, marginBottom:12, opacity:0.9}}>✅ Work day complete</div>
            <div className="checked-in-info">
              <div className="info-item">
                <div className="info-label">Checked in</div>
                <div className="info-value">{new Date(today.check_in).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Checked out</div>
                <div className="info-value">{new Date(today.check_out).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Regular hrs</div>
                <div className="info-value">{today.regular_hours}h</div>
              </div>
              {today.overtime_hours > 0 && (
                <div className="info-item">
                  <div className="info-label">Overtime</div>
                  <div className="info-value">+{today.overtime_hours}h</div>
                </div>
              )}
            </div>
          </div>
        ) : checkedIn ? (
          <div>
            <div style={{fontSize:14,marginBottom:12,opacity:0.9}}>
              ✅ Checked in at {new Date(today.check_in).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
              {today.is_wfh && ' (WFH)'}
            </div>
            <button className="checkin-btn" onClick={handleCheckOut} disabled={loading}>
              {loading ? 'Processing...' : 'Check Out'}
            </button>
          </div>
        ) : (
          <button className="checkin-btn" onClick={handleCheckIn} disabled={loading}>
            {loading ? 'Processing...' : 'Check In'}
          </button>
        )}
      </div>

      {/* Manager/admin dashboard stats */}
      {dashboard && (
        <div className="stat-grid" style={{marginBottom:24}}>
          <div className="stat-card"><div className="stat-num">{dashboard.present}</div><div className="stat-label">Present Today</div></div>
          <div className="stat-card"><div className="stat-num">{dashboard.on_leave}</div><div className="stat-label">On Leave</div></div>
          <div className="stat-card"><div className="stat-num">{dashboard.wfh}</div><div className="stat-label">WFH</div></div>
          <div className="stat-card"><div className="stat-num" style={{color:'var(--danger)'}}>{dashboard.absent}</div><div className="stat-label">Absent</div></div>
          <div className="stat-card"><div className="stat-num" style={{color:'var(--warning)'}}>{dashboard.late_arrivals}</div><div className="stat-label">Late Arrivals</div></div>
        </div>
      )}

      {/* Monthly summary */}
      {summary && (
        <div className="card">
          <div className="card-title">📊 This Month</div>
          <div className="stat-grid" style={{margin:0}}>
            <div className="stat-card"><div className="stat-num">{summary.present_days || 0}</div><div className="stat-label">Days Present</div></div>
            <div className="stat-card"><div className="stat-num">{summary.wfh_days || 0}</div><div className="stat-label">WFH Days</div></div>
            <div className="stat-card"><div className="stat-num">{parseFloat(summary.total_regular || 0).toFixed(1)}</div><div className="stat-label">Regular Hours</div></div>
            <div className="stat-card"><div className="stat-num" style={{color:'var(--success)'}}>{parseFloat(summary.total_overtime || 0).toFixed(1)}</div><div className="stat-label">Overtime Hours</div></div>
          </div>
        </div>
      )}
    </div>
  );
}
